import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Instagram, Loader2 } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { CONTACT, INTEREST_OPTIONS } from "../data/content";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  project_address: "",
  interest: "",
  details: "",
  timeline: "",
};

const inputClass =
  "rounded-none border-white/20 bg-black/40 px-4 py-6 text-white placeholder:text-gray-600 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0";

export const InquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        timeline: form.timeline || null,
      });
      setSubmitted(true);
      setForm(EMPTY);
      toast.success("Thank you — we've received your project inquiry and will be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong sending your inquiry. Please call us at 516-886-6002.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="inquiry-section" className="grain relative border-t border-white/10 bg-ink py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <SectionHeader eyebrow="Start Your Project" title="Have a Project in Mind?">
            Whether you're considering a complete renovation, home addition, major transformation,
            or building a new custom home, we'd like to hear about your project.
          </SectionHeader>

          <Reveal delay={0.2}>
            <div className="mt-12 space-y-6 border-t border-white/10 pt-10">
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone-link"
                className="group flex items-center gap-5 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <span className="flex h-12 w-12 items-center justify-center border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-black">
                  <Phone className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-accent text-[10px] font-semibold uppercase tracking-[0.25em] text-gray-500">Call</span>
                  <span className="mt-1 block text-lg text-white transition-colors duration-300 group-hover:text-gold">
                    {CONTACT.phone}
                  </span>
                </span>
              </a>
              <a
                href={CONTACT.emailHref}
                data-testid="contact-email-link"
                className="group flex items-center gap-5 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <span className="flex h-12 w-12 items-center justify-center border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-black">
                  <Mail className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-accent text-[10px] font-semibold uppercase tracking-[0.25em] text-gray-500">Email</span>
                  <span className="mt-1 block text-lg text-white transition-colors duration-300 group-hover:text-gold">
                    {CONTACT.email}
                  </span>
                </span>
              </a>
              <a
                href={CONTACT.instagramHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="group flex items-center gap-5 focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <span className="flex h-12 w-12 items-center justify-center border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-black">
                  <Instagram className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-accent text-[10px] font-semibold uppercase tracking-[0.25em] text-gray-500">Instagram</span>
                  <span className="mt-1 block text-lg text-white transition-colors duration-300 group-hover:text-gold">
                    {CONTACT.instagram}
                  </span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="inquiry-success-message"
              className="flex h-full min-h-[420px] flex-col items-center justify-center border border-gold/40 bg-ink-2 p-12 text-center"
            >
              <span className="gold-rule mx-auto" aria-hidden="true" />
              <h3 className="mt-8 font-serif text-3xl font-medium text-white md:text-4xl">
                Thank You.
              </h3>
              <p className="mt-6 max-w-md text-base leading-relaxed text-gray-400">
                Your project inquiry has been received. We'll review the details and reach out to
                you shortly to talk about what's possible.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="inquiry-submit-another-button"
                className="mt-10 border border-white/25 px-8 py-3.5 font-accent text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold"
              >
                Submit Another Inquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="inquiry-form"
              className="border border-white/10 bg-ink-2 p-8 md:p-12"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="inquiry-name" className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    Name *
                  </Label>
                  <Input id="inquiry-name" data-testid="inquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-phone" className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    Phone Number *
                  </Label>
                  <Input id="inquiry-phone" data-testid="inquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(516) 555-0123" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-email" className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    Email *
                  </Label>
                  <Input id="inquiry-email" data-testid="inquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-address" className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    Project Address / Town *
                  </Label>
                  <Input id="inquiry-address" data-testid="inquiry-address-input" required value={form.project_address} onChange={set("project_address")} placeholder="e.g. Garden City, NY" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    What Are You Interested In? *
                  </Label>
                  <Select required value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                    <SelectTrigger data-testid="inquiry-interest-select" className={`${inputClass} h-auto`}>
                      <SelectValue placeholder="Select a project type" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-white/10 bg-ink-3 text-white">
                      {INTEREST_OPTIONS.map((option) => (
                        <SelectItem
                          key={option}
                          value={option}
                          data-testid={`inquiry-interest-option-${option.toLowerCase().replace(/\s+/g, "-")}`}
                          className="focus:bg-gold focus:text-black"
                        >
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-timeline" className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    Estimated Project Timeline <span className="text-gray-600">(Optional)</span>
                  </Label>
                  <Input id="inquiry-timeline" data-testid="inquiry-timeline-input" value={form.timeline} onChange={set("timeline")} placeholder="e.g. Within 6 months" className={inputClass} />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="inquiry-details" className="font-accent text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                    Tell Us About Your Project *
                  </Label>
                  <Textarea
                    id="inquiry-details"
                    data-testid="inquiry-details-input"
                    required
                    rows={5}
                    value={form.details}
                    onChange={set("details")}
                    placeholder="Share your vision — the scope, the style, what matters most to you."
                    className={`${inputClass} py-4`}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit-button"
                className="mt-10 inline-flex w-full items-center justify-center gap-3 bg-gold px-10 py-5 font-accent text-xs font-semibold uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold-hover focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-ink-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {submitting ? "Sending..." : "Start Your Project"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
